<template>
  <div class="p-info-tip" ref="wrapper">
    <slot />
    <transition name="p-info-tip-fade" @after-leave="onAfterLeave">
      <div
        v-if="isVisible"
        ref="popover"
        class="p-info-tip__popover"
        :class="[`p-info-tip__popover--${placement}`]"
        :style="popoverStyle"
        @click.stop
      >
        <div class="p-info-tip__arrow" />
        <div class="p-info-tip__body">
          <div class="p-info-tip__text">
            <div v-if="title" class="p-info-tip__title">{{ title }}</div>
            <div v-if="message" class="p-info-tip__message" v-html="message"></div>
          </div>
          <button v-if="dismissible" class="p-info-tip__close" @click="dismiss">
            <CIcon name="cipClose" size="custom-size" width="16" height="16" />
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'PInfoTip',
  props: {
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    dismissible: {
      type: Boolean,
      default: false
    },
    trigger: {
      type: String,
      default: 'hover',
      validator: value => ['click', 'hover'].includes(value)
    },
    autoDisplay: {
      type: Boolean,
      default: false
    },
    placement: {
      type: String,
      default: 'right',
      validator: value => ['top', 'right', 'bottom', 'left'].includes(value)
    },
    attachTo: {
      type: String,
      default: 'parent',
      validator: value => ['parent', 'body'].includes(value)
    }
  },
  data() {
    return {
      isVisible: false,
      popoverStyle: null
    }
  },
  computed: {
    isBodyMode() {
      return this.attachTo === 'body'
    }
  },
  watch: {
    isVisible(val) {
      if (!this.isBodyMode) return
      if (val) {
        this.$nextTick(() => this.moveToBody())
      }
    }
  },
  mounted() {
    if (this.autoDisplay) {
      this.$nextTick(() => { this.isVisible = true })
    }

    const el = this.$refs.wrapper
    if (!el) return

    if (this.trigger === 'hover') {
      el.addEventListener('mouseenter', this.show)
      el.addEventListener('mouseleave', this.hide)
    } else {
      el.addEventListener('click', this.toggle)
    }
  },
  beforeDestroy() {
    this.cleanupBody()

    const el = this.$refs.wrapper
    if (!el) return

    el.removeEventListener('mouseenter', this.show)
    el.removeEventListener('mouseleave', this.hide)
    el.removeEventListener('click', this.toggle)
  },
  methods: {
    show() {
      this.isVisible = true
    },
    hide() {
      this.isVisible = false
    },
    toggle() {
      this.isVisible = !this.isVisible
    },
    dismiss() {
      this.isVisible = false
      this.$emit('dismiss')
    },

    moveToBody() {
      const popover = this.$refs.popover
      if (!popover) return

      document.body.appendChild(popover)
      this.updateBodyPosition()
      window.addEventListener('scroll', this.updateBodyPosition, true)
      window.addEventListener('resize', this.updateBodyPosition)
    },

    cleanupBody() {
      window.removeEventListener('scroll', this.updateBodyPosition, true)
      window.removeEventListener('resize', this.updateBodyPosition)

      const popover = this.$refs.popover
      if (popover && popover.parentElement === document.body) {
        document.body.removeChild(popover)
      }
    },

    onAfterLeave() {
      if (this.isBodyMode) {
        this.cleanupBody()
      }
    },

    updateBodyPosition() {
      const popover = this.$refs.popover
      const wrapper = this.$refs.wrapper
      if (!popover || !wrapper) return

      const rect = wrapper.getBoundingClientRect()
      const gap = 4
      let top, left

      switch (this.placement) {
        case 'right':
          left = rect.right + gap
          top = rect.top + rect.height / 2
          this.popoverStyle = {
            position: 'fixed',
            left: `${left}px`,
            top: `${top}px`,
            transform: 'translateY(-50%)'
          }
          break
        case 'left':
          left = rect.left - gap
          top = rect.top + rect.height / 2
          this.popoverStyle = {
            position: 'fixed',
            right: `${window.innerWidth - left}px`,
            top: `${top}px`,
            transform: 'translateY(-50%)'
          }
          break
        case 'top':
          left = rect.left + rect.width / 2
          top = rect.top - gap
          this.popoverStyle = {
            position: 'fixed',
            left: `${left}px`,
            bottom: `${window.innerHeight - top}px`,
            transform: 'translateX(-50%)'
          }
          break
        case 'bottom':
          left = rect.left + rect.width / 2
          top = rect.bottom + gap
          this.popoverStyle = {
            position: 'fixed',
            left: `${left}px`,
            top: `${top}px`,
            transform: 'translateX(-50%)'
          }
          break
      }
    }
  }
}
</script>

<style scoped lang="scss">
.p-info-tip {
  position: relative;
  display: inline-flex;
  cursor: pointer;
}

.p-info-tip__popover {
  position: absolute;
  z-index: 1050;
  display: flex;
  align-items: center;
  filter: drop-shadow(0px 0.8px 0.8px rgba(0, 0, 0, 0.04))
          drop-shadow(0px 2.3px 2px rgba(0, 0, 0, 0.03));

  &--right {
    left: calc(100% + 4px);
    top: 50%;
    transform: translateY(-50%);
    flex-direction: row;
  }

  &--left {
    right: calc(100% + 4px);
    top: 50%;
    transform: translateY(-50%);
    flex-direction: row-reverse;
  }

  &--top {
    bottom: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    flex-direction: column-reverse;
  }

  &--bottom {
    top: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    flex-direction: column;
  }
}

.p-info-tip__arrow {
  width: 0;
  height: 0;
  flex-shrink: 0;

  .p-info-tip__popover--right & {
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #1A1254;
  }

  .p-info-tip__popover--left & {
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid #1A1254;
  }

  .p-info-tip__popover--top & {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #1A1254;
  }

  .p-info-tip__popover--bottom & {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid #1A1254;
  }
}

.p-info-tip__body {
  background-color: #1A1254;
  border-radius: 12px;
  padding: 12px 16px 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 200px;
  max-width: 300px;
}

.p-info-tip__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.p-info-tip__title {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: #FFFFFF;
}

.p-info-tip__message {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.12px;
  color: #FFFFFF;
}

.p-info-tip__close {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #E1DFFB;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  outline: none;

  &:hover {
    color: #FFFFFF;
  }
}

.p-info-tip-fade-enter-active,
.p-info-tip-fade-leave-active {
  transition: opacity 0.2s ease;
}

.p-info-tip-fade-enter,
.p-info-tip-fade-leave-to {
  opacity: 0;
}
</style>
